// src/components/games/HypeStacker.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Coins, ArrowLeft, Trophy, Zap } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

// Block configuration constants
const BLOCK_HEIGHT = '5vh';
const MIN_TOLERANCE = 0.02;
const ENTRY_FEE = 10; // Cost to play in CRD

interface HypeStackerProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  updateProfile: (data: any) => Promise<void>;
  onBuyCredits: () => void;
}

export const HypeStacker: React.FC<HypeStackerProps> = ({ 
  isOpen, 
  onClose, 
  currentUser, 
  updateProfile,
  onBuyCredits 
}) => {
  const [gameState, setGameState] = useState('START');
  const [score, setScore] = useState(0);
  const [multiplier, setMultiplier] = useState(1);
  const [combo, setCombo] = useState(0);
  const [stack, setStack] = useState<any[]>([]);
  const [conveyor, setConveyor] = useState<any[]>([]);
  const [flash, setFlash] = useState(false);
  const [message, setMessage] = useState('');
  const [credits, setCredits] = useState(0);
  const [totalWon, setTotalWon] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs for high-performance animation loop
  const requestRef = useRef<any>();
  const stateRef = useRef({
    conveyorX: 0,
    speed: 3,
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 400,
    screenHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
    stack: [] as any[],
    conveyor: [] as any[]
  });

  useEffect(() => {
    if (currentUser?.cred !== undefined) {
      setCredits(currentUser.cred);
    }
  }, [currentUser]);

  // Handle Resize
  useEffect(() => {
    const handleResize = () => {
      stateRef.current.screenWidth = window.innerWidth;
      stateRef.current.screenHeight = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate conveyor blocks (VEGAS VIP COLORS)
  const generateConveyorBlocks = (startX: number, count: number, currentScreenWidth: number) => {
    const types = [
      { wPct: 0.12, mult: 5, color: 'linear-gradient(180deg, #34d399 0%, #059669 50%, #064e3b 100%)', glow: 'rgba(5, 150, 105, 0.6)', border: '#6ee7b7', label: '+5 CRD' }, 
      { wPct: 0.25, mult: 3, color: 'linear-gradient(180deg, #f87171 0%, #dc2626 50%, #991b1b 100%)', glow: 'rgba(220, 38, 38, 0.6)', border: '#fca5a5', label: '+3 CRD' }, 
      { wPct: 0.40, mult: 1, color: 'linear-gradient(180deg, #4b5563 0%, #1f2937 50%, #030712 100%)', glow: 'rgba(75, 85, 99, 0.6)', border: '#9ca3af', label: '+1 CRD' }  
    ];

    let currentX = startX;
    let newBlocks = [];
    
    for (let i = 0; i < count; i++) {
      const type = types[Math.floor(Math.random() * types.length)];
      const width = type.wPct * currentScreenWidth;
      const gap = (Math.random() * 0.15 + 0.1) * currentScreenWidth; 
      
      newBlocks.push({
        id: Math.random().toString(),
        relativeX: currentX,
        width: width,
        multiplier: type.mult,
        label: type.label,
        color: type.color,
        glow: type.glow,
        border: type.border
      });
      
      currentX += width + gap;
    }
    return { blocks: newBlocks, nextX: currentX };
  };

  // Game Loop
  const updateLoop = useCallback(() => {
    if (gameState !== 'PLAYING') return;

    stateRef.current.conveyorX -= stateRef.current.speed;

    const lastBlock = stateRef.current.conveyor[stateRef.current.conveyor.length - 1];
    if (lastBlock && (lastBlock.relativeX + stateRef.current.conveyorX) < stateRef.current.screenWidth * 2) {
      const { blocks } = generateConveyorBlocks(
        lastBlock.relativeX + lastBlock.width + (stateRef.current.screenWidth * 0.15), 
        10, 
        stateRef.current.screenWidth
      );
      stateRef.current.conveyor = [...stateRef.current.conveyor, ...blocks];
    }

    setConveyor([...stateRef.current.conveyor]);

    requestRef.current = requestAnimationFrame(updateLoop);
  }, [gameState]);

  useEffect(() => {
    if (gameState === 'PLAYING') {
      requestRef.current = requestAnimationFrame(updateLoop);
    }
    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, updateLoop]);

  if (!isOpen) return null;

  const startGame = async () => {
    if (credits < ENTRY_FEE) {
      setMessage(`Need ${ENTRY_FEE} CRD to play!`);
      setTimeout(() => setMessage(''), 2000);
      return;
    }

    setIsProcessing(true);
    
    const newBalance = credits - ENTRY_FEE;
    setCredits(newBalance);
    await updateProfile({ cred: newBalance });

    const sw = stateRef.current.screenWidth;
    const initialStack = [{
      x: sw * 0.3,
      width: sw * 0.4,
      color: 'linear-gradient(180deg, #ffe066 0%, #f59e0b 50%, #92400e 100%)',
      glow: 'rgba(245, 158, 11, 0.6)',
      border: '#fef08a',
      multiplier: 1,
      label: 'BASE'
    }];
    
    const { blocks } = generateConveyorBlocks(sw, 20, sw);
    
    stateRef.current = {
      ...stateRef.current,
      conveyorX: 0,
      speed: sw * 0.009, 
      stack: initialStack,
      conveyor: blocks
    };

    setStack(initialStack);
    setConveyor(blocks);
    setScore(0);
    setMultiplier(1);
    setCombo(0);
    setTotalWon(0);
    setMessage('');
    setGameState('PLAYING');
    setIsProcessing(false);
  };

  const handleTap = async (e: any) => {
    if (e && e.cancelable) e.preventDefault(); 
    
    if (gameState !== 'PLAYING') {
      return;
    }

    const currentStack = stateRef.current.stack;
    const topBlock = currentStack[currentStack.length - 1];
    const topBlockLeft = topBlock.x;
    const topBlockRight = topBlock.x + topBlock.width;

    const activeConveyorBlock = stateRef.current.conveyor.find((block: any) => {
      const blockLeft = block.relativeX + stateRef.current.conveyorX;
      const blockRight = blockLeft + block.width;
      return blockRight > topBlockLeft && blockLeft < topBlockRight;
    });

    if (!activeConveyorBlock) {
      triggerGameOver("WHIFFED!");
      return;
    }

    const blockLeft = activeConveyorBlock.relativeX + stateRef.current.conveyorX;
    const blockRight = blockLeft + activeConveyorBlock.width;

    let overlapLeft = Math.max(topBlockLeft, blockLeft);
    let overlapRight = Math.min(topBlockRight, blockRight);
    let newWidth = overlapRight - overlapLeft;

    const tolerance = stateRef.current.screenWidth * 0.03;
    let isPerfect = false;
    
    if (Math.abs(blockLeft - topBlockLeft) < tolerance && Math.abs(blockRight - topBlockRight) < tolerance) {
      overlapLeft = topBlockLeft;
      newWidth = topBlock.width;
      isPerfect = true;
    }

    if (newWidth < stateRef.current.screenWidth * MIN_TOLERANCE) {
      triggerGameOver("SHATTERED!");
      return;
    }

    if (newWidth <= 0) {
      triggerGameOver("MISSED!");
      return;
    }

    const newBlock = {
      x: overlapLeft,
      width: newWidth,
      color: activeConveyorBlock.color,
      glow: activeConveyorBlock.glow,
      border: activeConveyorBlock.border,
      multiplier: activeConveyorBlock.multiplier,
      label: activeConveyorBlock.label
    };

    const newStack = [...currentStack, newBlock];
    stateRef.current.stack = newStack;
    setStack(newStack);

    let points = activeConveyorBlock.multiplier * multiplier; 
    if (isPerfect) {
      points *= 2;
      setCombo(prev => prev + 1);
      if (combo + 1 >= 3) {
        setMultiplier(prev => prev + 1);
      }
      triggerFlash("PERFECT!");
    } else {
      setCombo(0);
      setMultiplier(1);
    }
    
    const newTotal = score + points;
    setScore(newTotal);
    setTotalWon(newTotal);
    stateRef.current.speed += stateRef.current.screenWidth * 0.0009; 
  };

  const triggerGameOver = async (msg: string) => {
    cancelAnimationFrame(requestRef.current);
    setMessage(msg);
    setGameState('OVER');
    
    if (totalWon > 0) {
      const finalBalance = credits + totalWon;
      setCredits(finalBalance);
      await updateProfile({ cred: finalBalance });
      
      try {
        await addDoc(collection(db, "notifications"), {
          userId: currentUser.uid,
          type: 'reward',
          title: 'STACKER WIN! 🏗️',
          message: `You stacked ${totalWon} CRD in Hype Stacker!`,
          read: false,
          createdAt: serverTimestamp()
        });
      } catch (e) {
        console.error("Failed to save notification:", e);
      }
    }
    
    if (navigator.vibrate) navigator.vibrate([200, 100, 200]); 
  };

  const triggerFlash = (msg: string) => {
    setMessage(msg);
    setFlash(true);
    if (navigator.vibrate) navigator.vibrate(50);
    setTimeout(() => {
      setFlash(false);
      setMessage('');
    }, 400);
  };

  const stackHeightStr = BLOCK_HEIGHT;
  const blockHeightPx = (5 * stateRef.current.screenHeight) / 100;
  const verticalOffset = Math.max(0, (stack.length * blockHeightPx) - (stateRef.current.screenHeight * 0.4));

  return (
    <div className="fixed inset-0 z-[500] bg-[#1a0008] overflow-hidden select-none touch-none font-sans">
      
      {/* 🔥 TOP BAR - Back button left, Balance right */}
      <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-3 sm:px-4 py-3 bg-black/80 backdrop-blur-xl border-b border-red-500/20">
        <button
          onClick={onClose}
          className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-black/60 border border-red-500/30 rounded-xl text-white hover:bg-red-500/20 hover:border-red-400 transition-all duration-300 group"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform duration-300" />
          <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest">Back</span>
        </button>

        <div className="flex items-center gap-2 bg-black/60 border border-red-500/30 rounded-xl px-3 sm:px-4 py-2">
          <Coins size={16} className="text-yellow-400" />
          <span className="text-white font-bold font-mono text-xs sm:text-sm">{credits} CRD</span>
        </div>
      </div>

      {/* Invisible Tap Overlay for Gameplay */}
      {gameState === 'PLAYING' && (
        <div 
          className="absolute inset-0 z-30 touch-none"
          onPointerDown={handleTap}
        />
      )}

      {/* Custom Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes floatParticles {
          0% { transform: translateY(0) scale(1); opacity: 0; }
          50% { opacity: 0.5; }
          100% { transform: translateY(-100vh) scale(1.5); opacity: 0; }
        }
        .casino-panel {
          background: linear-gradient(180deg, #1f2937 0%, #111827 100%);
          border: 2px solid #b45309;
          border-top: 2px solid #fcd34d;
          box-shadow: 0 10px 30px rgba(0,0,0,0.9), inset 0 2px 10px rgba(253,230,138,0.2);
        }
        .gold-text {
          background: linear-gradient(to bottom, #fef08a 20%, #f59e0b 50%, #b45309 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 2px 4px rgba(0,0,0,0.8));
        }
      `}} />

      {/* Rich Velvet Casino Background Layers */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#4c0519_0%,#1a0008_80%,#000_100%)] pointer-events-none"></div>
      
      {/* Gold Dust Particles */}
      <div className="absolute inset-0 pointer-events-none opacity-30 overflow-hidden" style={{ backgroundImage: 'radial-gradient(#fcd34d 1px, transparent 1px)', backgroundSize: '50px 50px', backgroundPosition: '0 0' }}></div>

      {/* Flash Effect */}
      {flash && <div className="absolute inset-0 bg-yellow-200 opacity-40 pointer-events-none z-50 transition-opacity duration-75 mix-blend-overlay"></div>}

      {/* Casino Slot Machine HUD - Below top bar */}
      <div className="absolute top-14 left-0 w-full p-2 flex justify-between items-start z-40 pointer-events-none bg-black/40 border-b border-yellow-600/30 shadow-[0_4px_20px_rgba(0,0,0,0.5)]">
        
        {/* Jackpot Display */}
        <div className="flex flex-col casino-panel p-2 rounded-lg min-w-[120px] md:min-w-[180px]">
          <div className="flex justify-between items-center mb-0.5 space-x-2">
            <span className="text-yellow-500 text-[8px] md:text-[10px] tracking-widest uppercase font-bold">Total Winnings</span>
          </div>
          <span className="text-xl md:text-3xl font-black tracking-tight font-serif gold-text">
            {totalWon} CRD
          </span>
        </div>

        {/* Multiplier Display */}
        <div className="flex flex-col items-end casino-panel p-2 rounded-lg lg:px-4">
          <span className="text-yellow-500 text-[8px] md:text-[10px] tracking-widest uppercase font-bold mb-0.5">Multiplier</span>
          <span className={`text-xl md:text-3xl font-black tracking-tight font-serif ${multiplier > 1 ? 'gold-text' : 'text-gray-400'}`}>
            {multiplier}x
          </span>
        </div>
      </div>

      {/* Center Messages */}
      <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none text-center w-full">
        <h1 className={`text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase font-serif
          ${gameState === 'OVER' ? 'gold-text' : 'gold-text'}`}
          style={{ 
            filter: gameState === 'OVER' ? 'drop-shadow(0 0 20px rgba(220,38,38,0.8))' : 'drop-shadow(0 0 10px rgba(245,158,11,0.5))' 
          }}>
          {message}
        </h1>
        {gameState === 'OVER' && (
          <div className="mt-6 pointer-events-auto">
            <button 
              onClick={startGame}
              className="pointer-events-auto cursor-pointer bg-gradient-to-b from-yellow-400 to-yellow-600 border-2 border-yellow-200 text-black font-black uppercase tracking-widest py-3 px-8 lg:py-4 lg:px-12 lg:text-xl rounded-full shadow-[0_0_20px_rgba(245,158,11,0.6)] hover:scale-105 active:scale-95 transition-transform"
            >
              Play Again ({ENTRY_FEE} CRD)
            </button>
          </div>
        )}
        {gameState === 'START' && (
          <div className="mt-16 casino-panel p-6 md:p-8 lg:p-12 rounded-2xl inline-block shadow-[0_20px_50px_rgba(0,0,0,0.8)] border-yellow-500 pointer-events-auto">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight font-serif mb-2 gold-text">
              HIGH ROLLER
            </h2>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-black tracking-tight font-serif mb-2 text-white uppercase">
              Stacker
            </h3>
            <p className="text-zinc-400 text-xs md:text-sm mb-4">Cost: {ENTRY_FEE} CRD to play</p>
            
            <button 
              onClick={startGame}
              disabled={isProcessing || credits < ENTRY_FEE}
              className="mb-4 pointer-events-auto cursor-pointer bg-gradient-to-b from-yellow-400 to-yellow-600 border-2 border-yellow-200 text-black text-lg md:text-xl lg:text-2xl font-black uppercase tracking-widest py-3 px-10 lg:py-4 lg:px-14 rounded-full shadow-[0_0_20px_rgba(245,158,11,0.6)] hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {credits < ENTRY_FEE ? 'Need CRD' : 'Start Game'}
            </button>

            {credits < ENTRY_FEE && (
              <button
                onClick={onBuyCredits}
                className="text-xs text-yellow-400 hover:text-yellow-300 underline underline-offset-2 font-bold uppercase tracking-widest pointer-events-auto"
              >
                Buy More CRD →
              </button>
            )}

            <div className="flex flex-col space-y-2 text-[10px] md:text-xs font-bold tracking-widest uppercase items-center mt-4">
                <span className="bg-green-900/50 text-green-300 border border-green-600 px-4 py-1 rounded w-full shadow-[inset_0_0_8px_rgba(34,197,94,0.3)]">+5 CRD (TINY)</span>
                <span className="bg-red-900/50 text-red-300 border border-red-600 px-4 py-1 rounded w-full shadow-[inset_0_0_8px_rgba(239,68,68,0.3)]">+3 CRD (MID)</span>
                <span className="bg-gray-900/50 text-gray-300 border border-gray-600 px-4 py-1 rounded w-full shadow-[inset_0_0_8px_rgba(156,163,175,0.3)]">+1 CRD (LARGE)</span>
            </div>
          </div>
        )}
      </div>

      <div 
        className="absolute bottom-12 left-0 w-full h-full pointer-events-none transition-transform duration-300 ease-out"
        style={{ transform: `translateY(${verticalOffset}px)` }}
      >
        {/* The Placed Stack */}
        {stack.map((block: any, index: number) => (
          <div 
            key={`stack-${index}`} 
            className="absolute bottom-0 flex items-center justify-center overflow-hidden transition-all duration-100"
            style={{
              left: `${block.x}px`,
              width: `${block.width}px`,
              height: stackHeightStr,
              bottom: `${index * blockHeightPx}px`,
              background: block.color,
              border: `1px solid ${block.border}`,
              borderTopWidth: '2px',
              borderBottomWidth: '2px',
              borderBottomColor: 'rgba(0,0,0,0.8)',
              borderRadius: '6px',
              boxShadow: `0 4px 10px rgba(0,0,0,0.5), inset 0 2px 5px rgba(255,255,255,0.4), 0 0 15px ${block.glow}`,
              zIndex: 10 + index
            }} 
          >
             <div className="absolute top-0 left-0 w-full h-[50%] bg-gradient-to-b from-white/40 to-transparent rounded-t-md"></div>
             <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:10px_100%] opacity-30"></div>
             
             <span className="text-white font-black tracking-wider text-[10px] md:text-sm relative z-10" style={{ textShadow: '0 2px 2px rgba(0,0,0,0.8)' }}>
                {block.label}
             </span>
          </div>
        ))}

        {/* The Conveyor Belt */}
        {gameState === 'PLAYING' && (
          <div 
            className="absolute"
            style={{
              bottom: `${stack.length * blockHeightPx}px`,
              left: 0,
              width: '100%',
              height: stackHeightStr,
              zIndex: 50
            }}
          >
            {stack.length > 0 && (
               <div 
                 className="absolute h-[150vh] bottom-0"
                 style={{
                   left: `${stack[stack.length-1].x}px`,
                   width: `${stack[stack.length-1].width}px`,
                   background: 'linear-gradient(to top, rgba(255,215,0,0.15) 0%, transparent 100%)',
                   borderLeft: '1px solid rgba(255,215,0,0.3)',
                   borderRight: '1px solid rgba(255,215,0,0.3)',
                   pointerEvents: 'none'
                 }}
               />
            )}

            {conveyor.map((block: any) => {
              const currentScreenX = block.relativeX + stateRef.current.conveyorX;
              if (currentScreenX + block.width < -100 || currentScreenX > stateRef.current.screenWidth + 100) return null;
              
              return (
                <div 
                  key={block.id}
                  className="absolute bottom-0 flex items-center justify-center overflow-hidden"
                  style={{
                    left: `${currentScreenX}px`,
                    width: `${block.width}px`,
                    height: stackHeightStr,
                    background: block.color,
                    border: `1px solid ${block.border}`,
                    borderTopWidth: '2px',
                    borderBottomWidth: '2px',
                    borderBottomColor: 'rgba(0,0,0,0.8)',
                    borderRadius: '6px',
                    boxShadow: `0 8px 15px rgba(0,0,0,0.6), inset 0 2px 5px rgba(255,255,255,0.4), 0 0 20px ${block.glow}`,
                  }} 
                >
                  <div className="absolute top-0 left-0 w-full h-[50%] bg-gradient-to-b from-white/40 to-transparent rounded-t-md"></div>
                  <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:10px_100%] opacity-30"></div>

                  <span className="text-white font-black tracking-wider text-[10px] md:text-sm relative z-10" style={{ textShadow: '0 2px 2px rgba(0,0,0,0.8)' }}>
                    {block.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Legend */}
      <div className="absolute bottom-0 left-0 w-full p-2 flex justify-between text-[8px] md:text-[10px] lg:text-xs p-2 font-bold tracking-widest uppercase bg-gradient-to-t from-black to-transparent z-40">
        <span className="text-gray-300 drop-shadow-[0_1px_2px_rgba(0,0,0,1)] flex items-center"><span className="w-2 h-2 rounded-full bg-gray-600 mr-1 border border-gray-400"></span> +1 CRD</span>
        <span className="text-red-300 drop-shadow-[0_1px_2px_rgba(0,0,0,1)] flex items-center"><span className="w-2 h-2 rounded-full bg-red-500 mr-1 border border-red-400"></span> +3 CRD</span>
        <span className="text-green-300 drop-shadow-[0_1px_2px_rgba(0,0,0,1)] flex items-center"><span className="w-2 h-2 rounded-full bg-green-500 mr-1 border border-green-400 shadow-[0_0_5px_#22c55e]"></span> +5 CRD</span>
      </div>
    </div>
  );
};

export default HypeStacker;